import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { TYPES, iso, rangeFor, shift, rangeLabel } from "@/components/calendar/calUtils";
import { MonthView } from "@/components/calendar/MonthView";
import { TimeGrid } from "@/components/calendar/TimeGrid";
import { AgendaView } from "@/components/calendar/AgendaView";
import { EventDetail } from "@/components/calendar/EventDetail";
import { EventFormDialog, EMPTY_EVENT } from "@/components/calendar/EventFormDialog";
import { CalendarFilters, applyFilters } from "@/components/calendar/CalendarFilters";

const VIEWS = [["month", "Month"], ["week", "Week"], ["day", "Day"], ["agenda", "Agenda"]];
const today0 = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), d.getDate()); };

export default function Calendar() {
  const { user } = useAuth();
  const role = user?.role;
  const isDirector = role === "director";
  const isTeacher = role === "teacher";
  const [view, setView] = useState(() => (window.innerWidth < 640 ? "agenda" : "month"));
  const [cursor, setCursor] = useState(() => { const q = new URLSearchParams(window.location.search).get("date"); if (q && /^\d{4}-\d{2}-\d{2}$/.test(q)) { const [y, m, d] = q.split("-").map(Number); return new Date(y, m - 1, d); } return today0(); });
  const [data, setData] = useState({ items: [], closures: [] });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ type: "", dancer: "", cls: "", teacher: "", room: "" });
  const [scope, setScope] = useState(isTeacher ? "mine" : "all");
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(null);

  const [start, end] = rangeFor(view, cursor);
  const key = `${iso(start)}|${iso(end)}`;
  const load = useCallback(() => {
    setLoading(true);
    api.get("/calendar/feed", { params: { start: iso(start), end: iso(end) } })
      .then((r) => setData(r.data)).catch(() => toast.error("Couldn't load the calendar")).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => { load(); }, [load]);
  const [deep, setDeep] = useState(() => new URLSearchParams(window.location.search).get("event"));
  useEffect(() => {
    if (!deep || loading) return;
    const it = data.items.find((i) => i.uid === deep);
    if (it) setDetail(it);
    setDeep(null);
  }, [deep, loading, data.items]);

  const items = useMemo(() => applyFilters(data.items, filters, isTeacher && scope === "mine"), [data.items, filters, scope, isTeacher]);
  const byDate = useMemo(() => {
    const m = {}; items.forEach((i) => { (m[i.date] = m[i.date] || []).push(i); }); return m;
  }, [items]);
  const closures = useMemo(() => new Set(data.closures), [data.closures]);

  const openDay = (d) => { setCursor(new Date(d.getFullYear(), d.getMonth(), d.getDate())); setView("day"); };
  const respond = async (it, status, dancerId) => {
    try {
      await api.post(`/production/rehearsals/${it.source_id}/respond`, dancerId ? { status, dancer_id: dancerId } : { status });
      toast.success(status === "cannot" ? "Marked can't attend" : "Marked attending");
      const did = dancerId || (it.my_dancers || [])[0]?.id;
      setDetail({ ...it, my_dancers: (it.my_dancers || []).map((d) => (d.id === did ? { ...d, status } : d)) });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const goView = (v) => { setView(v); if (v === "month") setCursor((c) => new Date(c.getFullYear(), c.getMonth(), Math.min(c.getDate(), 28))); };

  return (
    <div className="stagger-in">
      <PageHeader title="Calendar" subtitle={isDirector ? "Every class, practice, rehearsal and studio event in one place." : isTeacher ? "Your teaching and rehearsal schedule." : "Classes, practices, rehearsals and studio events for your family."} testid="calendar-page"
        action={isDirector && <Button data-testid="add-event-button" onClick={() => setForm({ ...EMPTY_EVENT, start_date: iso(cursor) })} className="rounded-full bg-teal hover:bg-teal-dark"><Plus className="w-4 h-4 mr-2" />Add Event</Button>} />

      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="rounded-full" data-testid="cal-today" onClick={() => setCursor(today0())}>Today</Button>
          <Button size="sm" variant="ghost" aria-label="Previous" className="rounded-full h-9 w-9 p-0" data-testid="cal-prev" onClick={() => setCursor(shift(view, cursor, -1))}><ChevronLeft className="w-5 h-5" /></Button>
          <Button size="sm" variant="ghost" aria-label="Next" className="rounded-full h-9 w-9 p-0" data-testid="cal-next" onClick={() => setCursor(shift(view, cursor, 1))}><ChevronRight className="w-5 h-5" /></Button>
          <h2 className="font-head font-bold text-lg sm:text-xl" data-testid="cal-range-label">{rangeLabel(view, cursor)}</h2>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" data-testid="cal-loading" />}
        </div>
        <div className="flex rounded-full bg-white border p-0.5" data-testid="cal-view-switch">
          {VIEWS.map(([v, l]) => (
            <button key={v} type="button" data-testid={`cal-view-${v}`} onClick={() => goView(v)}
              className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-colors ${view === v ? "bg-teal text-white" : "text-gray-600 hover:text-gray-900"}`}>{l}</button>))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        {isTeacher && <div className="flex rounded-full bg-white border p-0.5" data-testid="cal-scope">
          {[["mine", "My schedule"], ["all", "All teachers"]].map(([v, l]) => (
            <button key={v} type="button" data-testid={`cal-scope-${v}`} onClick={() => setScope(v)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${scope === v ? "bg-gray-900 text-white" : "text-gray-600"}`}>{l}</button>))}
        </div>}
        <CalendarFilters items={data.items} filters={filters} setFilters={setFilters} showPeople={!(isTeacher && scope === "mine")} />
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] mb-3 text-muted-foreground" data-testid="cal-legend">
        {Object.entries(TYPES).map(([k, v]) => <span key={k} className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: v.bg }} />{v.label}</span>)}
      </div>

      {view === "month" && <MonthView cursor={cursor} byDate={byDate} closures={closures} onOpen={setDetail} onDay={openDay} onAdd={isDirector ? (ds) => setForm({ ...EMPTY_EVENT, start_date: ds }) : null} />}
      {(view === "week" || view === "day") && <TimeGrid view={view} cursor={cursor} byDate={byDate} closures={closures} onOpen={setDetail} onDay={openDay} />}
      {view === "agenda" && <AgendaView cursor={cursor} byDate={byDate} onOpen={setDetail} />}

      <EventDetail it={detail} onClose={() => setDetail(null)} onRespond={respond} onEditEvent={(e) => setForm({ ...EMPTY_EVENT, ...e })} role={role} isDirector={isDirector} />
      <EventFormDialog form={form} setForm={setForm} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />
    </div>);
}
